import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScaleTime } from 'd3-scale';
import { scaleTime } from 'd3-scale';
import useEventListener from '@use-it/event-listener';

export interface Day {
    start: Date;
    end: Date;
    name: string;
    key: string;
}

interface UseCalendarScalesReturn {
    graphGridRef: RefObject<HTMLDivElement>;
    scalesPerDay: Map<Day, ScaleTime<number, number>> | null;
}

export function useCalendarScales(days: Day[]): UseCalendarScalesReturn {
    const graphGridRef = useRef<HTMLDivElement>(null);
    const graphGridWidthRef = useRef<number>();
    const [scalesPerDay, setScalesPerDay] = useState<Map<Day, ScaleTime<number, number>> | null>(
        null
    );

    const calculateScalesPerDay = useCallback((): void => {
        if (!graphGridRef.current) return;

        const { width } = graphGridRef.current.getBoundingClientRect();

        // Store the used width to prevent unnecessary recalculation triggered
        // by the resize event while the width stays the same.
        graphGridWidthRef.current = width;

        setScalesPerDay(
            new Map(
                days.map((day) => [day, scaleTime().domain([day.start, day.end]).range([0, width])])
            )
        );
    }, [days]);

    useEffect(() => {
        calculateScalesPerDay();
    }, [calculateScalesPerDay]);

    useEventListener('resize', () => {
        if (!graphGridRef.current) return;

        const { width } = graphGridRef.current.getBoundingClientRect();

        if (width !== graphGridWidthRef.current) {
            calculateScalesPerDay();
        }
    });

    return {
        graphGridRef,
        scalesPerDay,
    };
}
